import type { Ref } from '@nuxtjs/composition-api';
import { ref, readonly, useContext } from '@nuxtjs/composition-api';
import { Logger } from '~/helpers/logger';
import type { UseTrustedShopReviewsInterface, UseTrustedShopReviewsError } from './useTrustedShopReviews';
import axios from 'axios';
import { useCache, CacheTagPrefix } from '@vue-storefront/cache';
import { useUiState } from '~/composables';

/**
 * Allows loading reviews from trusted shops products.
 */
export function useTrustedShopReviews(): UseTrustedShopReviewsInterface {
  const { app } = useContext();
  const shopReviewsLoading = ref(false);
  const prodReviewsLoading = ref(false);

  const prodAvgRating = ref(0.0);
  const prodTotalReview = ref(0);

  const { addTags } = useCache();
  const { setTrustedShopProductReviews } = useUiState();

  const error: Ref<UseTrustedShopReviewsError> = ref({
    allReviews: null,
    prodReviews: null,
  });

  const allReviews = async () => {
    let results = null;

    try {
      shopReviewsLoading.value = true;

      let data = await app.$vsf.$magento.api.getAllTrustedShopReviews();

      results = data;

      error.value.allReviews = null;
    } catch (err) {
      error.value.allReviews = err;
      // Logger.error('useTrustedShopReviews/allReviews', err);
    } finally {
      shopReviewsLoading.value = false;
    }

    return results;
  };

  const emptyProductRating = () => {
    setTrustedShopProductReviews(0, 0);
  };

  const updateProductRating = (items) => {
    let total = 0;
    let sum = 0;

    if (items.length > 0) {
      items.forEach((el) => {
        sum += el.rating;
        total++;
      });

      setTrustedShopProductReviews(sum / total, total);
    }
  };

  const productReviews = async (sku: string, product: any) => {
    let results = null;
    let limit = 1000;

    try {
      prodReviewsLoading.value = true;

      let data = await app.$vsf.$magento.api.getProductTrustedShopReviews(product.name);
      let newDataItems = [];
      emptyProductRating();
      if (data?.items?.length > 0) {
        newDataItems = data.items.filter((el) => el.product.sku == sku);
        let labelsArray = [];
        if (
          product &&
          product.configurable_product_options_selection &&
          !product.configurable_product_options_selection.variant
        ) {
          if (product.variants) {
            let configurableOptionsArray = product.variants;
            labelsArray = configurableOptionsArray.map((obj) => obj.product.sku);
            data.items.filter((el) => {
              labelsArray.forEach(function (label) {
                if (el.product.sku == label) {
                  newDataItems.push(el);
                }
              });
            });
          }
        }
        updateProductRating(newDataItems);
      }
      results = {
        items: newDataItems,
      };

      error.value.prodReviews = null;
    } catch (err) {
      error.value.prodReviews = err;
      Logger.error('useTrustedShopReviews/productReviews', err);
    } finally {
      prodReviewsLoading.value = false;
    }

    return results;
  };

  return {
    allReviews,
    productReviews,
    shopReviewsLoading: readonly(shopReviewsLoading),
    prodReviewsLoading: readonly(prodReviewsLoading),
    error: readonly(error),
  };
}

export default useTrustedShopReviews;
export * from './useTrustedShopReviews';
